import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ContractPreview, emptyContract, type ContractFormState } from "@/components/admin/ContractBuilder";
import { CheckCircle2, PenLine, Eraser, X } from "lucide-react";
import { finalizeSignedContract } from "@/lib/contracts.functions";

export const Route = createFileRoute("/sign/contract/$token")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Sign contract — Recovery Trauma™" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: SignPage,
});

function SignPage() {
  const { token } = Route.useParams();
  const finalize = useServerFn(finalizeSignedContract);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [signature, setSignature] = useState("");
  const [mode, setMode] = useState<"typed" | "drawn">("typed");
  const [drawnData, setDrawnData] = useState<string>("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [declined, setDeclined] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: res, error } = await supabase.rpc("get_contract_for_signing", { _token: token });
        if (error) throw error;
        const parsed = res as any;
        if (!parsed?.found) { setError("This signing link is invalid or has expired."); return; }
        if (parsed?.expired) { setError("This signing link has expired. Please request a new one."); return; }
        setData(parsed);
        setName(parsed.counterparty_name ?? "");
        setEmail(parsed.counterparty_email ?? "");
        setSignature(parsed.counterparty_name ?? "");
        if (parsed.counterparty_signed) setDone(true);
        if (parsed.status === "declined") setDeclined(true);
        // Track view (fire and forget)
        supabase.rpc("mark_contract_viewed", { _token: token });
      } catch (e: any) {
        setError(e.message);
      } finally { setLoading(false); }
    })();
  }, [token]);

  const submit = async () => {
    if (!agreed) { toast.error("Please confirm you agree to the terms"); return; }
    if (mode === "drawn" && !drawnData) { toast.error("Please draw your signature"); return; }
    if (mode === "typed" && signature.trim().length < 2) { toast.error("Please type your signature"); return; }
    setSubmitting(true);
    try {
      const { error } = await supabase.rpc("sign_contract_as_counterparty", {
        _token: token,
        _signer_name: name,
        _signer_email: email,
        _signature_text: mode === "typed" ? signature : name,
        _ip: "",
        _ua: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : "",
        _signature_image: mode === "drawn" ? (drawnData ?? undefined) : undefined,
      });
      if (error) throw error;
      setDone(true);
      toast.success("Contract signed. Thank you.");
      // Finalize (PDF + emails) in the background
      finalize({ data: { token } }).catch(() => {});
    } catch (e: any) {
      toast.error(e.message);
    } finally { setSubmitting(false); }
  };

  const decline = async () => {
    if (declineReason.trim().length < 3) { toast.error("Please provide a brief reason"); return; }
    setSubmitting(true);
    try {
      const { error } = await supabase.rpc("decline_contract_as_counterparty", {
        _token: token,
        _signer_email: email,
        _reason: declineReason,
        _ip: "",
        _ua: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : "",
      });
      if (error) throw error;
      setDeclined(true);
      setShowDecline(false);
    } catch (e: any) {
      toast.error(e.message);
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-dvh bg-muted p-8 text-center text-sm text-muted-foreground">Loading contract…</div>;
  if (error) return <div className="min-h-dvh bg-muted p-8 text-center text-sm text-red-700">{error}</div>;
  if (!data) return null;

  const value: ContractFormState = {
    ...emptyContract,
    title: data.title, type: "custom",
    counterparty_name: data.counterparty_name, counterparty_email: data.counterparty_email ?? "",
    counterparty_role: data.counterparty_role ?? "", counterparty_company: data.counterparty_company ?? "",
    company_name: data.company_name, start_date: data.start_date ?? "", end_date: data.end_date ?? "",
    notice_period: data.notice_period ?? "", payment_terms: data.payment_terms ?? "",
    payment_frequency: data.payment_frequency ?? "",
    hourly_rate: data.hourly_rate?.toString() ?? "", hourly_rate_currency: data.hourly_rate_currency ?? "GBP",
    revenue_share: Array.isArray(data.revenue_share) ? data.revenue_share : [],
    governing_law: data.governing_law ?? "", notes: "",
    body: Array.isArray(data.body) ? data.body : [],
    custom_sections: Array.isArray(data.custom_sections) ? data.custom_sections : [],
  };

  const signatures: Array<{ signer_type: string; signer_name: string; signed_at: string }> = [];
  if (data.founder_signed) signatures.push({ signer_type: "founder", signer_name: data.founder_signature.signer_name, signed_at: data.founder_signature.signed_at });
  if (data.counterparty_signed) signatures.push({ signer_type: "counterparty", signer_name: data.counterparty_signature.signer_name, signed_at: data.counterparty_signature.signed_at });

  return (
    <div className="min-h-dvh bg-muted py-8">
      <div className="mx-auto max-w-3xl px-4">
        <header className="mb-6 text-center">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Recovery Trauma™</p>
          <h1 className="mt-1 font-display text-2xl text-foreground">Review & Sign Contract</h1>
          <p className="mt-1 text-sm text-muted-foreground">Please read the agreement carefully before signing.</p>
          {data.sign_expires_at && !done && !declined && (
            <p className="mt-2 text-xs text-muted-foreground">Signing link expires {new Date(data.sign_expires_at).toLocaleString()}</p>
          )}
        </header>

        <ContractPreview value={value} signatures={signatures} />

        <div className="mt-6 rounded-xl border border-border bg-card p-6">
          {declined ? (
            <div className="text-center">
              <X className="mx-auto mb-2 h-10 w-10 text-red-600" />
              <p className="font-medium text-foreground">This contract has been declined.</p>
              <p className="mt-1 text-sm text-muted-foreground">Recovery Trauma™ has been notified.</p>
            </div>
          ) : done ? (
            <div className="text-center">
              <CheckCircle2 className="mx-auto mb-2 h-10 w-10 text-emerald-600" />
              <p className="font-medium text-foreground">Thank you — this contract has been signed.</p>
              <p className="mt-1 text-sm text-muted-foreground">A signed PDF is being generated and will be emailed to you and Recovery Trauma™ shortly.</p>
            </div>
          ) : showDecline ? (
            <div className="space-y-3">
              <h2 className="font-display text-lg text-foreground">Decline this contract</h2>
              <p className="text-sm text-muted-foreground">Please provide a brief reason. Recovery Trauma™ will be notified.</p>
              <textarea value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} rows={4}
                className="w-full rounded border border-border bg-card px-3 py-2 text-sm" placeholder="Reason for declining…" />
              <div className="flex gap-2">
                <button onClick={decline} disabled={submitting}
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">
                  {submitting ? "Sending…" : "Decline contract"}
                </button>
                <button onClick={() => setShowDecline(false)} className="rounded-lg border px-4 py-2 text-sm">Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <h2 className="mb-3 font-display text-lg text-foreground flex items-center gap-2">
                <PenLine className="h-5 w-5" /> Sign this contract
              </h2>
              <p className="mb-4 text-sm text-muted-foreground">
                By signing below and clicking "Sign contract", you provide your legally recognised electronic signature. Your name, timestamp, and technical metadata will be recorded.
              </p>
              <div className="space-y-3">
                <label className="block text-sm">Full name
                  <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full rounded border border-border bg-card px-3 py-2 text-sm" />
                </label>
                <label className="block text-sm">Email
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full rounded border border-border bg-card px-3 py-2 text-sm" />
                </label>

                <div className="flex gap-2 text-xs">
                  <button type="button" onClick={() => setMode("typed")}
                    className={`rounded-full px-3 py-1 border ${mode === "typed" ? "bg-foreground text-background border-foreground" : "bg-card text-foreground border-border"}`}>Typed signature</button>
                  <button type="button" onClick={() => setMode("drawn")}
                    className={`rounded-full px-3 py-1 border ${mode === "drawn" ? "bg-foreground text-background border-foreground" : "bg-card text-foreground border-border"}`}>Drawn signature</button>
                </div>

                {mode === "typed" ? (
                  <label className="block text-sm">Typed signature
                    <input value={signature} onChange={(e) => setSignature(e.target.value)} className="mt-1 w-full rounded border border-border bg-card px-3 py-2 font-signature text-lg italic" />
                  </label>
                ) : (
                  <SignaturePad value={drawnData} onChange={setDrawnData} />
                )}

                <label className="flex items-start gap-2 text-sm text-foreground">
                  <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1" />
                  <span>I have read the contract and agree to be bound by its terms. I confirm that my signature is legally binding.</span>
                </label>
                <div className="flex flex-col-reverse gap-2 sm:flex-row">
                  <button onClick={() => setShowDecline(true)} className="rounded-lg border border-border py-3 px-4 text-sm text-foreground hover:bg-muted">
                    Decline
                  </button>
                  <button
                    onClick={submit}
                    disabled={submitting || !name || !agreed}
                    className="flex-1 rounded-lg bg-foreground py-3 text-sm font-medium text-background hover:bg-foreground disabled:opacity-50"
                  >
                    {submitting ? "Signing…" : "Sign contract"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SignaturePad({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#0f172a";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
  }, []);

  const pos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
    drawing.current = true;
    last.current = pos(e);
  };
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current!.getContext("2d")!;
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current!.x, last.current!.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const end = () => {
    drawing.current = false;
    last.current = null;
    if (canvasRef.current) onChange(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    onChange("");
  };

  return (
    <div className="space-y-2">
      <div className="rounded border border-border bg-card">
        <canvas
          ref={canvasRef}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          className="block w-full touch-none"
          style={{ height: 160 }}
        />
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>Draw your signature above using your mouse, trackpad or finger.</span>
        <button type="button" onClick={clear} className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 hover:bg-muted">
          <Eraser className="h-3 w-3" /> Clear
        </button>
      </div>
      {value && <p className="text-xs text-emerald-700">Signature captured.</p>}
    </div>
  );
}
